import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdAdd,
  MdOutlineMarkEmailRead,
  MdPause,
  MdPlayArrow,
  MdStop,
  MdOutlineBolt,
  MdOutlineUnsubscribe,
  MdOutlineWarningAmber,
  MdDeleteOutline,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { EMAIL } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonTable,
  SkeletonWorkList,
} from "../components/crm/Skeletons";
import { formatDate, formatNumber, formatPercent } from "../components/crm/crmHelpers";

/*
  Central de e-mail.

  Três abas numa tela só, porque são três faces do mesmo assunto e separá-las
  em rotas faria a equipe perder de vista a terceira — a lista de descadastro,
  que é a que diz se as campanhas estão incomodando.

  O cartão de estado do envio fica no topo e é o primeiro a ser lido: uma fila
  parada por credencial errada é invisível de qualquer outro jeito, e o
  sintoma (campanha que nunca termina) aparece horas depois.
*/

const STATUS_META = {
  DRAFT: { label: "Rascunho", tone: "neutral" },
  SCHEDULED: { label: "Agendada", tone: "info" },
  SENDING: { label: "Enviando", tone: "warning" },
  SENT: { label: "Enviada", tone: "success" },
  PAUSED: { label: "Pausada", tone: "warning" },
  CANCELED: { label: "Cancelada", tone: "danger" },
};

const SendingHealth = () => {
  const { data, isLoading } = useQuery(["email", "status"], async () => {
    const { data: response } = await axios.get(EMAIL.STATUS);
    return response.data;
  });

  if (isLoading || !data) return null;

  const healthy = data.configured && data.reachable;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl px-4 py-2.5 text-xs ${
        healthy
          ? "bg-slate-50 text-slate-600"
          : "bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200"
      }`}
    >
      {!healthy ? <MdOutlineWarningAmber size={16} /> : null}
      <span className="font-semibold">
        {healthy
          ? "Envio operando"
          : data.configured
          ? "Servidor de e-mail não responde — nada está saindo"
          : "SMTP não configurado — nada está saindo"}
      </span>
      <span>até {formatNumber(data.ratePerHour)} e-mails/hora</span>
      <span>remetente: {data.marketingFrom}</span>
      {data.error ? <span className="font-mono">{data.error}</span> : null}
    </div>
  );
};

const CampaignsTab = () => {
  const queryClient = useQueryClient();
  const { isAdminOrOperator } = useApplication();

  const { data, isLoading, error, refetch } = useQuery(
    ["email", "campaigns"],
    async () => {
      const { data: response } = await axios.get(EMAIL.CAMPAIGNS);
      return response.data;
    },
    // Campanha em envio muda sozinha: o worker avança a fila a cada minuto.
    { refetchInterval: 30_000 }
  );

  const action = useMutation(
    ({ id, verb }) => axios.post(`${EMAIL.CAMPAIGN}/${id}/${verb}`),
    { onSettled: () => queryClient.invalidateQueries(["email"]) }
  );

  const remove = useMutation((id) => axios.delete(`${EMAIL.CAMPAIGN}/${id}`), {
    onSettled: () => queryClient.invalidateQueries(["email"]),
  });

  if (isLoading) return <SkeletonTable rows={5} cols={6} withAvatar={false} />;
  if (error) return <ErrorBlock message="Não foi possível carregar as campanhas." onRetry={refetch} />;

  if (!data.length) {
    return (
      <Card>
        <EmptyState
          icon={MdOutlineMarkEmailRead}
          title="Nenhuma campanha ainda"
          description="Crie a primeira escolhendo um segmento do CRM — por exemplo, os free ativos com mais de 10 agendamentos no mês."
        />
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[840px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Campanha</th>
              <th className="px-4 py-3">Situação</th>
              <th className="px-4 py-3 text-right">Enviados</th>
              <th className="px-4 py-3 text-right">Abertura</th>
              <th className="px-4 py-3 text-right">Clique</th>
              <th className="px-4 py-3 text-right">Saíram</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.map((campaign) => {
              const meta = STATUS_META[campaign.status] || STATUS_META.DRAFT;
              const editable = ["DRAFT", "SCHEDULED"].includes(campaign.status);

              return (
                <tr key={campaign.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <Link
                      to={editable ? `/emails/${campaign.id}/editar` : `/emails/${campaign.id}`}
                      className="block font-semibold text-slate-900 hover:text-pink-700"
                    >
                      {campaign.name}
                    </Link>
                    <span className="block truncate text-[11px] text-slate-500">
                      {campaign.subject} · {campaign.authorName} ·{" "}
                      {formatDate(campaign.createdAt)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-700">
                    {formatNumber(campaign.sentCount)}
                    <span className="text-[11px] font-normal text-slate-400">
                      /{formatNumber(campaign.totalRecipients)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-700">
                    {formatPercent(campaign.openRate)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-700">
                    {formatPercent(campaign.clickRate)}
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-semibold ${
                      campaign.unsubscribeRate > 2
                        ? "text-rose-600"
                        : "text-slate-700"
                    }`}
                    title={
                      campaign.unsubscribeRate > 2
                        ? "Acima de 2% é sinal de que o conteúdo ou o segmento está errado"
                        : undefined
                    }
                  >
                    {formatPercent(campaign.unsubscribeRate)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {isAdminOrOperator && campaign.status === "SENDING" ? (
                        <>
                          <button
                            type="button"
                            title="Pausar"
                            onClick={() => action.mutate({ id: campaign.id, verb: "pause" })}
                            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-amber-50 hover:text-amber-600"
                          >
                            <MdPause size={17} />
                          </button>
                          <button
                            type="button"
                            title="Cancelar o que falta enviar"
                            onClick={() => {
                              if (window.confirm("Cancelar o restante do envio?")) {
                                action.mutate({ id: campaign.id, verb: "cancel" });
                              }
                            }}
                            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                          >
                            <MdStop size={17} />
                          </button>
                        </>
                      ) : null}
                      {isAdminOrOperator && campaign.status === "PAUSED" ? (
                        <button
                          type="button"
                          title="Retomar"
                          onClick={() => action.mutate({ id: campaign.id, verb: "resume" })}
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-emerald-50 hover:text-emerald-600"
                        >
                          <MdPlayArrow size={18} />
                        </button>
                      ) : null}
                      {isAdminOrOperator && editable ? (
                        <button
                          type="button"
                          title="Excluir rascunho"
                          onClick={() => {
                            if (window.confirm("Excluir esta campanha?")) {
                              remove.mutate(campaign.id);
                            }
                          }}
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                        >
                          <MdDeleteOutline size={17} />
                        </button>
                      ) : null}
                      <Link
                        to={`/emails/${campaign.id}`}
                        className="rounded-lg px-2 py-1 text-xs font-semibold text-pink-700 hover:underline"
                      >
                        relatório
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const TriggersTab = () => {
  const queryClient = useQueryClient();
  const { isAdminOrOperator } = useApplication();

  const { data, isLoading } = useQuery(["email", "triggers"], async () => {
    const { data: response } = await axios.get(EMAIL.TRIGGERS);
    return response.data;
  });

  const toggle = useMutation(
    ({ id, isActive }) => axios.put(`${EMAIL.TRIGGERS}/${id}/status`, { isActive }),
    { onSettled: () => queryClient.invalidateQueries(["email", "triggers"]) }
  );

  if (isLoading) return <SkeletonWorkList rows={3} withHeader={false} />;

  if (!data?.length) {
    return (
      <Card>
        <EmptyState
          icon={MdOutlineBolt}
          title="Nenhuma automação"
          description="Uma automação manda e-mail sozinha quando a conta entra num estágio — por exemplo, 7 dias depois de virar 'free parado'."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {data.map((trigger) => (
        <Card key={trigger.id} className="flex items-center gap-3 p-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-slate-900">{trigger.name}</p>
              <Badge tone={trigger.isActive ? "success" : "neutral"}>
                {trigger.isActive ? "ativa" : "desligada"}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">
              Dispara {trigger.delayDays === 0 ? "assim que" : `${trigger.delayDays} dia(s) depois de`}{" "}
              a conta entrar em <strong>{trigger.stageLabel}</strong> · assunto:{" "}
              {trigger.subject}
            </p>
            <p className="text-[11px] text-slate-400">
              {formatNumber(trigger.queuedCount)} e-mail(s) já enfileirados
              {trigger.lastRunAt ? ` · última execução ${formatDate(trigger.lastRunAt)}` : ""}
            </p>
          </div>

          {isAdminOrOperator ? (
            <button
              type="button"
              onClick={() => toggle.mutate({ id: trigger.id, isActive: !trigger.isActive })}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                trigger.isActive
                  ? "border border-slate-200 text-slate-600 hover:border-rose-200 hover:text-rose-700"
                  : "bg-pink-600 text-white hover:bg-pink-700"
              }`}
            >
              {trigger.isActive ? "Desligar" : "Ligar"}
            </button>
          ) : null}
        </Card>
      ))}
    </div>
  );
};

const SuppressionsTab = () => {
  const { data, isLoading } = useQuery(["email", "suppressions"], async () => {
    const { data: response } = await axios.get(EMAIL.SUPPRESSIONS);
    return response.data;
  });

  if (isLoading) return <SkeletonWorkList rows={3} withHeader={false} />;

  if (!data?.length) {
    return (
      <Card>
        <EmptyState
          icon={MdOutlineUnsubscribe}
          title="Ninguém saiu da lista"
          description="Quem clicar em 'não quero mais receber' aparece aqui e é excluído automaticamente de toda campanha futura."
        />
      </Card>
    );
  }

  const REASON = {
    UNSUBSCRIBED: { label: "Pediu para sair", tone: "warning" },
    BOUNCED: { label: "E-mail inexistente", tone: "danger" },
    COMPLAINED: { label: "Marcou como spam", tone: "danger" },
    MANUAL: { label: "Removido pelo staff", tone: "neutral" },
  };

  return (
    <Card className="overflow-hidden">
      <p className="border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
        Estes endereços são excluídos de toda campanha. Os avisos da conta
        (agendamento, cobrança, senha) continuam saindo normalmente — são
        execução do contrato, não marketing.
      </p>
      <ul className="divide-y divide-slate-100">
        {data.map((item) => {
          const meta = REASON[item.reason] || REASON.MANUAL;
          return (
            <li key={item.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
                {item.email}
              </span>
              <Badge tone={meta.tone}>{meta.label}</Badge>
              <span className="shrink-0 text-[11px] text-slate-400">
                {formatDate(item.createdAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const TABS = [
  { id: "campanhas", label: "Campanhas", Component: CampaignsTab },
  { id: "automacoes", label: "Automações", Component: TriggersTab },
  { id: "descadastros", label: "Descadastros", Component: SuppressionsTab },
];

const EmailsPage = () => {
  const [tab, setTab] = useState("campanhas");
  const Active = TABS.find((item) => item.id === tab).Component;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">E-mail</h1>
          <p className="text-sm text-slate-500">
            Campanhas para segmentos do CRM, automações por estágio e quem
            pediu para sair da lista.
          </p>
        </div>

        <Link
          to="/emails/nova"
          className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-pink-700"
        >
          <MdAdd size={18} /> Nova campanha
        </Link>
      </header>

      <SendingHealth />

      <div className="flex gap-1.5">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset transition ${
              tab === item.id
                ? "bg-slate-900 text-white ring-slate-900"
                : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-300"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <Active />
    </div>
  );
};

export default EmailsPage;
